import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CaretLeft, CaretRight, Wallet, HandCoins, Warning } from '@phosphor-icons/react';
import { PF, fmtEur, currentMonth } from './pfApi';
import { fmtMonth } from '@/utils/dateFmt';

function shiftMonth(month, delta) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function PfDashboard() {
  const [month, setMonth] = useState(currentMonth());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${PF}/summary`, { params: { month } });
      setSummary(data);
    } catch { setSummary(null); }
    setLoading(false);
  }, [month]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  return (
    <div data-testid="pf-dashboard-tab">
      <div className="flex items-center justify-center gap-3 mb-5">
        <button onClick={() => setMonth(m => shiftMonth(m, -1))} className="p-2 rounded-full hover:bg-brand-bg" data-testid="pf-dashboard-prev-month"><CaretLeft size={16} /></button>
        <span className="font-heading font-medium text-brand-text text-sm w-28 text-center" data-testid="pf-dashboard-month-label">{fmtMonth(month)}</span>
        <button onClick={() => setMonth(m => shiftMonth(m, 1))} className="p-2 rounded-full hover:bg-brand-bg" data-testid="pf-dashboard-next-month"><CaretRight size={16} /></button>
      </div>

      {loading && <p className="text-sm text-brand-muted text-center">A carregar...</p>}

      {summary && !loading && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="pf-card-total-pago">
              <p className="text-[11px] text-brand-muted mb-1">Total pago (com Duarte)</p>
              <p className="text-xl font-heading font-semibold text-brand-text">{fmtEur(summary.despesas_total_pago)}</p>
            </div>
            <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="pf-card-minha-despesa">
              <p className="text-[11px] text-brand-muted mb-1">A minha despesa real</p>
              <p className="text-xl font-heading font-semibold text-brand-green">{fmtEur(summary.minha_despesa_real)}</p>
            </div>
            <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="pf-card-a-receber">
              <p className="text-[11px] text-brand-muted mb-1 flex items-center gap-1"><HandCoins size={13} /> A receber do Duarte</p>
              <p className="text-xl font-heading font-semibold text-amber-600">{fmtEur(summary.a_receber_duarte)}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-brand-green/5 border border-brand-green/20 rounded-2xl p-4" data-testid="pf-card-receitas">
              <p className="text-[11px] text-brand-muted mb-1">Receitas do mês</p>
              <p className="text-lg font-heading font-semibold text-brand-text">{fmtEur(summary.receitas)}</p>
            </div>
            <div className={`rounded-2xl p-4 border ${summary.saldo >= 0 ? 'bg-brand-green/5 border-brand-green/20' : 'bg-red-50 border-red-200'}`} data-testid="pf-card-saldo">
              <p className="text-[11px] text-brand-muted mb-1 flex items-center gap-1"><Wallet size={13} /> Saldo do mês</p>
              <p className={`text-lg font-heading font-semibold ${summary.saldo >= 0 ? 'text-brand-green' : 'text-red-600'}`}>{fmtEur(summary.saldo)}</p>
            </div>
          </div>

          {summary.poupanca !== undefined && summary.poupanca !== 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-5" data-testid="pf-card-poupanca">
              <p className="text-[11px] text-brand-muted mb-1">Poupança do mês (fora das despesas de consumo)</p>
              <p className="text-lg font-heading font-semibold text-blue-700">{fmtEur(summary.poupanca)}</p>
            </div>
          )}

          {summary.budget_alerts?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5" data-testid="pf-budget-alerts">
              <p className="text-xs font-medium text-amber-800 mb-2 flex items-center gap-1"><Warning size={14} /> Perto do orçamento definido</p>
              {summary.budget_alerts.map(a => (
                <p key={a.category_id} className="text-xs text-amber-700" data-testid={`pf-budget-alert-${a.category_id}`}>{a.name}: {fmtEur(a.spent)} de {fmtEur(a.budget)} ({a.pct}%)</p>
              ))}
            </div>
          )}

          {summary.breakdown_por_ambito?.length > 0 && (
            <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5">
              <p className="text-xs font-medium text-brand-muted mb-3">Despesas por âmbito</p>
              <div className="space-y-2">
                {summary.breakdown_por_ambito.map(a => {
                  const max = summary.breakdown_por_ambito[0]?.total || 1;
                  return (
                    <div key={a.ambito} data-testid={`pf-breakdown-ambito-${a.ambito}`}>
                      <div className="flex justify-between text-xs mb-1"><span className="text-brand-text">{a.ambito}</span><span className="font-medium text-brand-text">{fmtEur(a.total)}</span></div>
                      <div className="h-1.5 rounded-full bg-brand-bg overflow-hidden"><div className="h-full rounded-full bg-brand-green" style={{ width: `${Math.min(100, (a.total / max) * 100)}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bg-white border border-brand-border rounded-2xl p-4">
            <p className="text-xs font-medium text-brand-muted mb-3">Despesas por categoria (a minha parte)</p>
            {summary.breakdown_por_categoria.length === 0 && <p className="text-sm text-brand-placeholder">Sem despesas este mês.</p>}
            <div className="space-y-2">
              {summary.breakdown_por_categoria.map(c => {
                const max = summary.breakdown_por_categoria[0]?.total || 1;
                return (
                  <div key={c.category_id} data-testid={`pf-breakdown-${c.category_id}`}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-brand-text">{c.name}</span><span className="font-medium text-brand-text">{fmtEur(c.total)}</span></div>
                    <div className="h-1.5 rounded-full bg-brand-bg overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, (c.total / max) * 100)}%`, background: c.color }} /></div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
